import { ButtonStyle, ControlSize, ControlType, Property, PropertyType } from "../../../base/types";

import { DefaultLabel } from "../../widgets";
import { DesignerMode } from "../../types";
import { FC, useEffect, useState } from "react";
import { IControlAttributes } from "../../../base/models";
import { MUIIcon } from "../../../widgets";
import { Registry } from "../../../base/register";
import clsx from "clsx";
import { getButtonClass } from "../buttons/helper";
import { useGetProperties } from "../../../base/hooks";
import { Link } from "react-router-dom";

export const DownloadableDocument: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const label: string | undefined = properties[Property.Label];
    const tooltip: string | undefined = properties[Property.Tooltip];
    const url: string | undefined = properties[Property.Url];
    const target: string | undefined = properties[Property.Target];
    const icon: string | undefined = properties[Property.MaterialIcon]
    const style: ButtonStyle | undefined = properties[Property.BackgroundStyle]
    const size: ControlSize | undefined = properties[Property.Size]
    const enableDownload: boolean | undefined = properties[Property.EnableDownload]
    const enableDownloadByCRM: boolean | undefined = properties[Property.EnableDownloadByCRM]

    if (designerMode === DesignerMode.Draft) {
        return (
            <div className={clsx(getButtonClass(style, size, "btn btn-link btn-sm"), className)} title={tooltip}>
                {(icon) && <MUIIcon iconName={icon} className={clsx({ "me-1": !!label })} />}
                <DefaultLabel
                    control={control}
                    propertyId={Property.Label}
                    defaultLabel="Downloadable Document"
                    className="text-hover-primary bg-secondary" />
                {children}
            </div>)
    }

    return (
        <DownloadLinkCell fieldUrl={url || ''} children={children} label={label} icon={icon} target={target} enableDownload={enableDownload} enableDownloadByCRM={enableDownloadByCRM} className={className} />
    )
}

export const DownloadLinkCell = ({ fieldUrl, children, label, icon, target, enableDownload, enableDownloadByCRM, className }: {
    fieldUrl: string,
    label?: string,
    icon?: string,
    target?: string,
    className?: string,
    children?: React.ReactNode,
    enableDownload?: boolean,
    enableDownloadByCRM?: boolean
}) => {
    const [documentUrl, setDocumentUrl] = useState<string>()

    useEffect(() => {
        if (!fieldUrl) {
            return;
        }

        let url = fieldUrl
        if (url === undefined)
            return;

        if (url.startsWith("https://")) {
            url = new URL(url).pathname;
        }

        setDocumentUrl(url);
        // eslint-disable-next-line
    }, [fieldUrl])

    const downloadDocument = async (documentUrl: string) => {
        try {
            const response = await fetch(
                `/api/CRMDocuments/download?url=${encodeURIComponent(documentUrl)}`
            );

            if (!response.ok) {
                throw new Error("Download failed");
            }

            const blob = await response.blob();

            const contentDisposition = response.headers.get("content-disposition");
            let fileName = "document.pdf";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) {
                    fileName = match[1];
                }
            }

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        }
    };

    if (!documentUrl) {
        return <>{children}</>
    }

    return <>
        {enableDownload ? <Link
            className={className}
            target={target}
            download={enableDownload}
            to={`/api/documents/document?url=${encodeURIComponent(documentUrl)}`}>
            <span>{label}</span>
            {children}
        </Link> :
            enableDownloadByCRM && <a
                className={className}
                onClick={(e) => {
                    e.preventDefault();
                    downloadDocument(documentUrl);
                }}
            >
                <span>{label}</span>
                {children}
            </a>}
    </>
}

Registry.addControl({
    id: "5742f5ca-9201-463b-8ea8-cc2fafe8f1e9",
    name: "Downloadable Document",
    type: ControlType.DownloadableDocument,
    properties: [
        { id: Property.MaterialIcon, name: 'Icon', type: PropertyType.MaterialIcon },
        { id: Property.Label, name: 'Label', type: PropertyType.String },
        { id: Property.Url, name: 'Url', type: PropertyType.String },
        { id: Property.Target, name: 'Target', type: PropertyType.String },
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String },
        { id: Property.BackgroundStyle, name: 'Style', type: PropertyType.ButtonStyle },
        { id: Property.Size, name: 'Size', type: PropertyType.ControlSize },
        { id: Property.EnableDownload, name: 'Enable Download', type: PropertyType.Boolean },
        { id: Property.EnableDownloadByCRM, name: 'Enable Download by CRM', type: PropertyType.Boolean },
    ],
    component: DownloadableDocument
})
